'use client';

import { useEffect } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { authStore } from '@/infrastructure/store/authStore';
import { portalAcessoStore } from '@/infrastructure/store/portalAcessoStore';

import { sessionAudience, TSessionAudience } from '../helpers/sessionAudience';

interface IUsePortalAcessoResult {
  audience: TSessionAudience;
  /** Known to be factory staff: the only case in which the shell loads factory data. */
  isStaff: boolean;
  isBrandBuyer: boolean;
  /**
   * The brands whose Portal is open to a brand buyer; empty for staff. A buyer may have none:
   * the factory closed the Portal or inactivated the brand, and they still only reach the Portal.
   */
  marcas: Array<string>;
  loading: boolean;
  /** What the API said when it refused, empty when it did not answer at all. */
  lastError: string;
  load: () => Promise<void>;
}

/**
 * Who the shell is serving. A pure selector: the answer loads once, in
 * `usePortalAcessoBootstrap`, and the menu, the route gate and the shell loads all read it.
 */
export function usePortalAcesso(): IUsePortalAcessoResult {
  const { acesso, failed, loading, lastError, load } = useStore(
    portalAcessoStore,
    useShallow((s) => ({
      acesso: s.acesso,
      failed: s.failed,
      loading: s.loading,
      lastError: s.lastError,
      load: s.load,
    })),
  );
  const audience = sessionAudience(acesso, failed);

  return {
    audience,
    isStaff: audience === 'staff',
    isBrandBuyer: audience === 'brandBuyer',
    marcas: acesso?.marcas ?? [],
    loading,
    lastError,
    load,
  };
}

/**
 * Loads the answer ONCE per session and account, from the layout. Gated on the unified login
 * because the layout mounts outside `AuthGuard`, and keyed by the account because the brand
 * registry belongs to it: a buyer of one account may be staff of another.
 */
export function usePortalAcessoBootstrap(): void {
  const isAuthenticated = useStore(authStore, (s) => s.isAuthenticated);
  const partnerId = useStore(authStore, (s) => s.user?.partnerId ?? '');
  const load = useStore(portalAcessoStore, (s) => s.load);

  useEffect(() => {
    if (!isAuthenticated || partnerId === '') {
      return;
    }
    const run = async (): Promise<void> => {
      await load();
    };
    run();
  }, [isAuthenticated, partnerId, load]);
}
